import { useEffect, useMemo, useRef, useState } from "react";
import {
  BREAKDANCE_ELEMENTS,
  type ApiElementMarking,
  type CapturedNode,
} from "@clarity/shared";
import { treeToBreakdanceClipboard } from "../lib/breakdance-hifi";
import { api } from "../lib/api";

// Floating element inspector — opens at the clicked element's screen
// coords in Code Mode. Shows the captured selector, dimensions, and a
// curated set of computed styles, with a "Copy CSS" action so the
// owner can paste straight into a stylesheet — plus "Copy as
// Breakdance", which maps the captured subtree to a structured
// Breakdance element (Section/Div/Heading/Text/Button children).

export type InspectedElement = {
  tagName: string;
  id: string | null;
  classes: string[];
  selector: string;
  text: string;
  rect: { width: number; height: number };
  styles: Record<string, string>;
};

type Props = {
  anchor: { x: number; y: number };
  element: InspectedElement;
  // Captured subtree for Copy-as-Breakdance. Null when the overlay
  // bundle predates Phase 14 — the button simply doesn't render.
  tree: CapturedNode | null;
  // Owner's project id — enables the AI translate endpoint. Only set in
  // owner mode (Code Mode is owner-only), so the AI button is gated by
  // its presence.
  projectId?: string;
  // Current full page URL — sent with the AI translate so the worker can
  // apply this page's element tags.
  pageUrl?: string;
  // Phase 18 — the owner's Breakdance tag for this element (null if none),
  // and callbacks to set/clear it. Present only in owner mode.
  marking?: ApiElementMarking | null;
  onSetMarking?: (selector: string, breakdanceType: string) => Promise<void>;
  onClearMarking?: (selector: string) => Promise<void>;
  onClose: () => void;
};

const POPOVER_WIDTH = 360;
const ANCHOR_GAP = 12;

export function ElementInspector({
  anchor,
  element,
  tree,
  projectId,
  pageUrl,
  marking,
  onSetMarking,
  onClearMarking,
  onClose,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [bdState, setBdState] = useState<"idle" | "copied" | "failed">("idle");
  const [tagState, setTagState] = useState<"idle" | "saving" | "saved" | "failed">(
    "idle",
  );
  const [aiState, setAiState] = useState<
    "idle" | "busy" | "failed" | "unavailable"
  >("idle");
  // When set, the AI result modal is open showing this clipboard JSON.
  const [aiJson, setAiJson] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const node = ref.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      onClose();
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [onClose]);

  const placement = computePlacement(anchor);

  // Group the catalog by category once for the <optgroup>s.
  const tagGroups = useMemo(() => {
    const map = new Map<string, { slug: string; label: string }[]>();
    for (const e of BREAKDANCE_ELEMENTS) {
      const arr = map.get(e.category) ?? [];
      arr.push({ slug: e.slug, label: e.label });
      map.set(e.category, arr);
    }
    return Array.from(map.entries());
  }, []);
  const canTag = Boolean(onSetMarking && onClearMarking);

  const label = element.id
    ? `${element.tagName}#${element.id}`
    : element.classes.length > 0
      ? `${element.tagName}.${element.classes.slice(0, 3).join(".")}`
      : element.tagName;

  async function copyCss() {
    const css = formatCss(element);
    try {
      await navigator.clipboard.writeText(css);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  async function copyAsBreakdance() {
    if (!tree) return;
    try {
      await navigator.clipboard.writeText(treeToBreakdanceClipboard(tree));
      setBdState("copied");
      setTimeout(() => setBdState("idle"), 1500);
    } catch {
      setBdState("failed");
      setTimeout(() => setBdState("idle"), 1500);
    }
  }

  async function onChangeTag(next: string) {
    if (!onSetMarking || !onClearMarking) return;
    setTagState("saving");
    try {
      if (next === "") await onClearMarking(element.selector);
      else await onSetMarking(element.selector, next);
      setTagState("saved");
      setTimeout(() => setTagState("idle"), 1500);
    } catch {
      setTagState("failed");
      setTimeout(() => setTagState("idle"), 2000);
    }
  }

  async function copyAsBreakdanceAi() {
    if (!tree || !projectId || aiState === "busy") return;
    setAiState("busy");
    try {
      const { clipboard } = await api.translateBreakdance(projectId, tree, pageUrl);
      // Open the modal with the clean JSON so the owner sees exactly what
      // they're copying (no devtools-escaping confusion). Also try the
      // clipboard as a convenience; the modal's button is the reliable path.
      setAiJson(clipboard);
      void navigator.clipboard.writeText(clipboard).catch(() => {});
      setAiState("idle");
    } catch (err) {
      const status = (err as { status?: number }).status;
      setAiState(status === 503 ? "unavailable" : "failed");
      setTimeout(() => setAiState("idle"), 2500);
    }
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Element inspector"
      style={{
        position: "fixed",
        left: placement.left,
        top: placement.top,
        width: POPOVER_WIDTH,
        zIndex: 100,
      }}
      className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl ring-1 ring-black/5"
    >
      <header className="border-b border-neutral-100 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <code className="block break-all font-mono text-xs font-semibold text-neutral-900">
            {label}
          </code>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-0.5 flex h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900"
          >
            ×
          </button>
        </div>
        <div className="mt-1 text-[11px] text-neutral-500">
          {element.rect.width} × {element.rect.height}
          {element.text ? <span className="ml-2 text-neutral-400">— “{element.text.slice(0, 80)}”</span> : null}
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void copyCss()}
            className={
              "rounded-md border px-2.5 py-1 text-[11px] transition " +
              (copied
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100")
            }
          >
            {copied ? "Copied" : "Copy CSS"}
          </button>
          {tree ? (
            <button
              type="button"
              onClick={() => void copyAsBreakdance()}
              title="Copy this element (with its children) as a Breakdance element — paste straight into the Breakdance editor"
              className={
                "rounded-md border px-2.5 py-1 text-[11px] transition " +
                (bdState === "copied"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : bdState === "failed"
                    ? "border-red-300 bg-red-50 text-red-700"
                    : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100")
              }
            >
              {bdState === "copied"
                ? "Copied"
                : bdState === "failed"
                  ? "Failed"
                  : "Copy as Breakdance"}
            </button>
          ) : null}
          {tree && projectId ? (
            <button
              type="button"
              onClick={() => void copyAsBreakdanceAi()}
              disabled={aiState === "busy"}
              title="Use AI to translate this element into a higher-fidelity Breakdance element — handles styled buttons, images, and design properties the instant copy can't"
              className={
                "rounded-md border px-2.5 py-1 text-[11px] transition disabled:opacity-60 " +
                (aiState === "failed" || aiState === "unavailable"
                  ? "border-red-300 bg-red-50 text-red-700"
                  : "border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700")
              }
            >
              {aiState === "busy"
                ? "Translating…"
                : aiState === "unavailable"
                  ? "Not configured"
                  : aiState === "failed"
                    ? "Failed"
                    : "Copy as Breakdance (AI)"}
            </button>
          ) : null}
        </div>
      </header>

      {canTag ? (
        <div className="border-b border-neutral-100 bg-neutral-50/60 px-4 py-3">
          <div className="flex items-center justify-between">
            <label
              htmlFor="bd-element-tag"
              className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500"
            >
              Breakdance element
            </label>
            {tagState === "saving" ? (
              <span className="text-[10px] text-neutral-400">Saving…</span>
            ) : tagState === "saved" ? (
              <span className="text-[10px] text-emerald-600">Saved</span>
            ) : tagState === "failed" ? (
              <span className="text-[10px] text-red-600">Failed</span>
            ) : marking ? (
              <button
                type="button"
                onClick={() => void onChangeTag("")}
                className="text-[10px] text-neutral-500 underline hover:text-neutral-900"
              >
                ✕ clear tag
              </button>
            ) : null}
          </div>
          <select
            id="bd-element-tag"
            value={marking?.breakdanceType ?? ""}
            onChange={(e) => void onChangeTag(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs text-neutral-900 focus:border-neutral-900 focus:outline-none"
          >
            <option value="">— Untagged (use auto-detection) —</option>
            {tagGroups.map(([category, items]) => (
              <optgroup key={category} label={category}>
                {items.map((it) => (
                  <option key={it.slug} value={it.slug}>
                    {it.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="mt-1 text-[10px] leading-snug text-neutral-400">
            Tags override auto-detection when you Copy as Breakdance (AI).
          </p>
        </div>
      ) : null}

      {aiJson !== null ? (
        <BreakdanceJsonModal json={aiJson} onClose={() => setAiJson(null)} />
      ) : null}

      <div className="max-h-[420px] space-y-4 overflow-y-auto px-4 py-3">
        <Section title="Layout">
          <Row label="display" value={element.styles["display"]} />
          <Row label="position" value={element.styles["position"]} />
          <Row label="opacity" value={element.styles["opacity"]} />
          <Row label="transform" value={element.styles["transform"]} hideIfEmpty />
        </Section>

        <Section title="Typography">
          <Row label="font-family" value={element.styles["font-family"]} />
          <Row label="font-size" value={element.styles["font-size"]} />
          <Row label="font-weight" value={element.styles["font-weight"]} />
          <Row label="line-height" value={element.styles["line-height"]} />
          <Row label="letter-spacing" value={element.styles["letter-spacing"]} hideIfEmpty />
          <Row label="text-align" value={element.styles["text-align"]} />
          <ColorRow label="color" value={element.styles["color"]} />
        </Section>

        <Section title="Background">
          <ColorRow label="background-color" value={element.styles["background-color"]} />
          <Row label="background-image" value={element.styles["background-image"]} hideIfEmpty />
        </Section>

        <Section title="Spacing">
          <BoxRow
            label="padding"
            t={element.styles["padding-top"]}
            r={element.styles["padding-right"]}
            b={element.styles["padding-bottom"]}
            l={element.styles["padding-left"]}
          />
          <BoxRow
            label="margin"
            t={element.styles["margin-top"]}
            r={element.styles["margin-right"]}
            b={element.styles["margin-bottom"]}
            l={element.styles["margin-left"]}
          />
        </Section>

        <Section title="Border & effects">
          <Row label="border-radius" value={element.styles["border-radius"]} hideIfEmpty />
          <Row label="border-top" value={shortBorder(element)} hideIfEmpty />
          <Row label="box-shadow" value={element.styles["box-shadow"]} hideIfEmpty />
        </Section>

        <Section title="Selector">
          <code className="block break-all font-mono text-[11px] text-neutral-700">
            {element.selector}
          </code>
        </Section>
      </div>
    </div>
  );
}

// ---------- presentational pieces ----------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  hideIfEmpty = false,
}: {
  label: string;
  value: string | undefined;
  hideIfEmpty?: boolean;
}) {
  const v = (value ?? "").trim();
  if (hideIfEmpty && (!v || v === "none" || v === "normal" || v === "auto" || v === "0px")) {
    return null;
  }
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-mono text-[11px] text-neutral-500">{label}</span>
      <span className="break-all text-right font-mono text-[11px] text-neutral-900">
        {v || "—"}
      </span>
    </div>
  );
}

function ColorRow({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  const v = (value ?? "").trim();
  if (!v || v === "rgba(0, 0, 0, 0)" || v === "transparent") {
    return <Row label={label} value="transparent" />;
  }
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-mono text-[11px] text-neutral-500">{label}</span>
      <span className="flex items-center gap-1.5">
        <span
          className="h-3 w-3 shrink-0 rounded border border-neutral-200"
          style={{ background: v }}
          aria-hidden
        />
        <span className="font-mono text-[11px] text-neutral-900">{v}</span>
      </span>
    </div>
  );
}

function BoxRow({
  label,
  t,
  r,
  b,
  l,
}: {
  label: string;
  t: string | undefined;
  r: string | undefined;
  b: string | undefined;
  l: string | undefined;
}) {
  const tt = t ?? "0px";
  const rr = r ?? "0px";
  const bb = b ?? "0px";
  const ll = l ?? "0px";
  const allZero = [tt, rr, bb, ll].every((v) => v === "0px");
  if (allZero) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-mono text-[11px] text-neutral-500">{label}</span>
      <span className="font-mono text-[11px] text-neutral-900">
        {tt} {rr} {bb} {ll}
      </span>
    </div>
  );
}

function shortBorder(el: InspectedElement): string {
  const w = el.styles["border-top-width"]?.trim();
  const s = el.styles["border-top-style"]?.trim();
  const c = el.styles["border-top-color"]?.trim();
  if (!w || w === "0px" || s === "none") return "";
  return `${w} ${s} ${c}`;
}

function formatCss(el: InspectedElement): string {
  const lines: string[] = [];
  lines.push(`/* ${el.selector} */`);
  for (const [prop, value] of Object.entries(el.styles)) {
    const v = (value ?? "").trim();
    if (!v) continue;
    // Skip noise defaults that aren't useful to paste.
    if (v === "none" || v === "normal" || v === "auto" || v === "0px") continue;
    if (v === "rgba(0, 0, 0, 0)") continue;
    lines.push(`${prop}: ${v};`);
  }
  return lines.join("\n");
}

function computePlacement(anchor: { x: number; y: number }): {
  left: number;
  top: number;
} {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchor.x + ANCHOR_GAP;
  if (left + POPOVER_WIDTH > vw - 12) {
    left = Math.max(12, anchor.x - POPOVER_WIDTH - ANCHOR_GAP);
  }
  let top = anchor.y + ANCHOR_GAP;
  const ASSUMED_HEIGHT = 480;
  if (top + ASSUMED_HEIGHT > vh - 12) {
    top = Math.max(12, vh - ASSUMED_HEIGHT - 12);
  }
  return { left, top };
}

// Modal that shows the generated Breakdance clipboard JSON in a copyable
// <pre>. Beats a silent clipboard write: the owner sees the exact, clean
// JSON (no devtools double-escaping) and copies it deliberately. Pretty-
// printed for readability — Breakdance accepts the whitespace on paste.
function BreakdanceJsonModal({
  json,
  onClose,
}: {
  json: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  // Pretty-print for display; fall back to the raw string if it somehow
  // isn't parseable (it always is — code built it).
  let pretty = json;
  try {
    pretty = JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    /* keep raw */
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — the user can still select-all in the pre
    }
  }

  return (
    <div
      // Stop propagation so the inspector's outside-click handler doesn't
      // fire and close the inspector underneath.
      onMouseDown={(e) => e.stopPropagation()}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-6"
      role="dialog"
      aria-label="Breakdance JSON"
    >
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-neutral-900">
              Breakdance element
            </h2>
            <p className="mt-0.5 text-[11px] text-neutral-500">
              Copy this, then paste directly into the Breakdance editor.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void copy()}
              className={
                "rounded-md border px-3 py-1.5 text-xs transition " +
                (copied
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700")
              }
            >
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900"
            >
              ×
            </button>
          </div>
        </header>
        <pre className="m-0 flex-1 overflow-auto whitespace-pre-wrap break-words bg-neutral-50 px-4 py-3 font-mono text-[11px] leading-relaxed text-neutral-800">
          {pretty}
        </pre>
      </div>
    </div>
  );
}
